// Fonte única do nome e do rótulo de um evento.
// Nada aqui faz rede: só formatação a partir do que já veio do banco.

import { tipoEventoCanonico } from '@/lib/tipoEvento';

export interface ClienteDoEvento {
  nome?: string | null;
  papel?: string | null;
}

export interface EventoParaNome {
  tipo_evento?: string | null;
  titulo_evento?: string | null;
  nome_noiva?: string | null;
  nome_noivo?: string | null;
  clientes?: ClienteDoEvento[] | null;
}

const ROTULOS: Record<string, string> = {
  casamento: 'Casamento',
  aniversario_15: '15 anos',
  corporativo: 'Evento corporativo',
  aniversario: 'Aniversário',
  formatura: 'Formatura',
  batizado: 'Batizado',
  infantil: 'Infantil',
  bodas: 'Bodas',
};

export function tipoEventoLabel(tipo?: string | null): string {
  const canon = tipoEventoCanonico(tipo);
  if (!canon) return 'Evento';
  return ROTULOS[canon] ?? 'Evento';
}

export function isCasamento(tipo?: string | null): boolean {
  return tipoEventoCanonico(tipo) === 'casamento';
}

export function isQuinzeAnos(tipo?: string | null): boolean {
  return tipoEventoCanonico(tipo) === 'aniversario_15';
}

export function horarioPrincipalLabel(tipo?: string | null): string {
  return 'Horário do evento';
}

// Placeholders literais gravados pelo bug antigo de criação — tratados como vazio.
const PLACEHOLDERS = new Set(['noivo', 'noiva', 'aniversariante', 'cliente']);

function limpo(v?: string | null): string {
  const s = String(v ?? '').trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  if (PLACEHOLDERS.has(s.toLowerCase())) return '';
  return s;
}

function primeiroNome(v?: string | null): string {
  return limpo(v).split(/\s+/)[0] ?? '';
}

function clientePorPapel(evento: EventoParaNome, papel: string): string {
  const achado = (evento.clientes ?? []).find(
    (c) => String(c?.papel ?? '').trim() === papel,
  );
  return limpo(achado?.nome);
}

function comecaComRotulo(texto: string, rotulo: string): boolean {
  const t = texto.trim().toLowerCase();
  const r = rotulo.trim().toLowerCase();
  if (!r) return false;
  return t === r || t.startsWith(`${r} `);
}

// Nome do evento já com o rótulo do tipo (ex.: "Casamento Géssica & Lucas",
// "15 anos de Luiza"). Reaproveita a resolução de nomes de nomeDoEvento,
// sem nunca duplicar o rótulo quando o nome/título já começa com ele.
export function nomeDoEventoComTipo(evento?: EventoParaNome | null): string {
  const ev = evento ?? {};
  const titulo = limpo(ev.titulo_evento);
  const rotulo = tipoEventoLabel(ev.tipo_evento);
  const tipo = tipoEventoCanonico(ev.tipo_evento);

  if (tipo === 'casamento') {
    const noiva = limpo(ev.nome_noiva) || clientePorPapel(ev, 'noiva');
    const noivo = limpo(ev.nome_noivo) || clientePorPapel(ev, 'noivo');
    const nome = noiva && noivo ? `${noiva} & ${noivo}` : noiva || noivo || '';
    if (nome) {
      if (comecaComRotulo(nome, rotulo)) return nome;
      return `Casamento ${nome}`;
    }
    if (titulo) {
      if (comecaComRotulo(titulo, rotulo)) return titulo;
      return `Casamento ${titulo}`;
    }
    return 'Casamento';
  }

  if (tipo === 'aniversario_15') {
    // 1. Título já começa com o rótulo "15 anos" → devolvido como está.
    if (titulo && comecaComRotulo(titulo, rotulo)) return titulo;
    // 2. Aniversariante resolvido (nome_noiva ou cliente com papel aniversariante).
    const aniversariante = limpo(ev.nome_noiva) || clientePorPapel(ev, 'aniversariante');
    if (aniversariante) {
      if (comecaComRotulo(aniversariante, rotulo)) return aniversariante;
      return `15 anos de ${primeiroNome(aniversariante)}`;
    }
    // 3. Título sem o rótulo → prefixa.
    if (titulo) return `15 anos ${titulo}`;
    // 4. Fallback.
    return '15 anos';
  }

  // Demais tipos: rótulo + nome resolvido (nomeDoEvento), título ou só o rótulo.
  const base = nomeDoEvento(ev);
  if (base && base !== rotulo) {
    if (comecaComRotulo(base, rotulo)) return base;
    return `${rotulo} ${base}`;
  }
  if (titulo) {
    if (comecaComRotulo(titulo, rotulo)) return titulo;
    return `${rotulo} ${titulo}`;
  }
  return rotulo;
}

export function nomeDoEvento(evento?: EventoParaNome | null): string {
  const ev = evento ?? {};
  const titulo = limpo(ev.titulo_evento);
  const tipo = tipoEventoCanonico(ev.tipo_evento);

  if (tipo === 'casamento') {
    const noiva = limpo(ev.nome_noiva) || clientePorPapel(ev, 'noiva');
    const noivo = limpo(ev.nome_noivo) || clientePorPapel(ev, 'noivo');
    if (noiva && noivo) return `${noiva} & ${noivo}`;
    if (noiva) return noiva;
    if (noivo) return noivo;
    return titulo || 'Casamento';
  }

  if (tipo === 'aniversario_15') {
    if (titulo) return titulo;
    const aniversariante = limpo(ev.nome_noiva) || clientePorPapel(ev, 'aniversariante');
    if (aniversariante) return `15 anos de ${primeiroNome(aniversariante)}`;
    return '15 anos';
  }

  if (titulo) return titulo;
  const primeiro = limpo((ev.clientes ?? [])[0]?.nome);
  if (primeiro) return primeiro;
  return tipoEventoLabel(ev.tipo_evento);
}

export interface RotuloCliente {
  nome: string;
  o: string;
  pro: string;
  pelo: string;
  vocesOuVoce: string;
}

export function clienteLabel(tipo?: string | null): RotuloCliente {
  if (isCasamento(tipo)) {
    return { nome: 'casal', o: 'o casal', pro: 'pro casal', pelo: 'pelo casal', vocesOuVoce: 'vocês' };
  }

  return {
    nome: 'cliente',
    o: 'o cliente',
    pro: 'pro cliente',
    pelo: 'pelo cliente',
    vocesOuVoce: isQuinzeAnos(tipo) ? 'vocês' : 'você',
  };
}

/** Toast quando o evento não tem telefone do cliente: a mensagem foi copiada. */
export function avisoSemTelefone(rotulo: RotuloCliente, oQue: 'mensagem' | 'código' = 'mensagem'): string {
  const doCliente = `d${rotulo.o}`; // "o casal" → "do casal"; "o cliente" → "do cliente"
  const copiado = oQue === 'código' ? 'código copiado' : 'mensagem copiada';
  return `Evento sem telefone ${doCliente} — ${copiado}. Cole no WhatsApp.`;
}
